import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MediaMagicProvider, MagicImage } from '@media-magic/sdk';
import { getAnalytics, type AnalyticsData } from '../services/dashboardApi';

interface DashboardProps {
  userId: string;
}

const CDN_URL = import.meta.env.VITE_MM_CDN_URL || '';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatMs(value: number): string {
  const seconds = Math.max(0, value || 0) / 1000;
  return `${seconds.toFixed(seconds >= 10 ? 1 : 2)}s`;
}

export function Dashboard({ userId }: DashboardProps) {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await getAnalytics(userId);
        if (!cancelled) setAnalytics(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [userId]);

  const summary = analytics?.summary;
  const cacheRatio = analytics?.cacheHitRatio ?? 0;
  const recentImages = (analytics?.popularImages || []).slice(0, 5);

  return (
    <MediaMagicProvider config={{ clientId: userId, cdnUrl: CDN_URL, assets: ['assets', 'test-assets'] }}>
      <div className="dashboard-page">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p className="dashboard-greeting">Welcome back. Here's your workspace at a glance.</p>
        </div>

        {loading && (
          <div className="dashboard-loading">Loading dashboard...</div>
        )}

        {error && !analytics && (
          <div className="dashboard-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && analytics && (
          <>
            <section className="dashboard-kpis">
              <div className="dashboard-kpi">
                <span className="dashboard-kpi__label">Total Images</span>
                <span className="dashboard-kpi__value">{formatNumber(summary?.totalImages || 0)}</span>
              </div>
              <div className="dashboard-kpi">
                <span className="dashboard-kpi__label">Total Transforms</span>
                <span className="dashboard-kpi__value">{formatNumber(summary?.totalTransforms || 0)}</span>
              </div>
              <div className="dashboard-kpi">
                <span className="dashboard-kpi__label">Cache Hit Ratio</span>
                <span className="dashboard-kpi__value">{Math.round(cacheRatio * 100)}%</span>
              </div>
              <div className="dashboard-kpi">
                <span className="dashboard-kpi__label">Avg Transform Time</span>
                <span className="dashboard-kpi__value">{formatMs(summary?.avgTransformMs || 0)}</span>
              </div>
            </section>

            <section className="dashboard-actions">
              <h2>Quick Actions</h2>
              <div className="dashboard-actions__grid">
                <Link to="/gallery" className="dashboard-action-card">
                  <h3>Library</h3>
                  <p>Manage your images</p>
                  <span className="dashboard-action-card__stat">{formatNumber(summary?.totalImages || 0)} images</span>
                </Link>
                <Link to="/analytics" className="dashboard-action-card">
                  <h3>Insights</h3>
                  <p>View analytics &amp; performance</p>
                  <span className="dashboard-action-card__stat">Detailed metrics</span>
                </Link>
                <Link to="/demo" className="dashboard-action-card">
                  <h3>Demo</h3>
                  <p>Try the SDK playground</p>
                  <span className="dashboard-action-card__stat">Live preview</span>
                </Link>
                <Link to="/api-keys" className="dashboard-action-card">
                  <h3>API Keys</h3>
                  <p>Manage access credentials</p>
                  <span className="dashboard-action-card__stat">{formatNumber(summary?.apiKeyCount || 0)} keys</span>
                </Link>
              </div>
            </section>

            {recentImages.length > 0 && (
              <section className="dashboard-recent">
                <div className="dashboard-recent__header">
                  <h2>Recent Images</h2>
                  <Link to="/gallery" className="section-link">View all</Link>
                </div>
                <div className="dashboard-recent__grid">
                  {recentImages.map((img) => (
                    <Link key={img.mediaId} to="/gallery" className="dashboard-recent__item">
                      <MagicImage
                        src={img.mediaId}
                        alt={img.originalFilename || img.mediaId}
                        crop
                        autofocus="smart"
                        layout="responsive"
                        autoSize={true}
                        showStats={true}
                      />
                      <span className="dashboard-recent__label">
                        {img.originalFilename || img.mediaId.slice(0, 12)}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </MediaMagicProvider>
  );
}

export default Dashboard;
